import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../shadcn/accordion";
import ImageFallback from "../ImageWithFallback";

type NoxusItemFooter =
  | {
      type: "actions";
      label?: ReactNode;
      children: ReactNode;
    }
  | {
      type: "accordion";
      label: ReactNode;
      children: ReactNode;
      defaultOpen?: boolean;
    };

interface NoxusItemProps {
  icon?: LucideIcon | string;
  iconAlt?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: NoxusItemFooter;
  size?: "default" | "sm";
  className?: string;
  iconClassName?: string;
}

const NoxusItem = ({
  icon,
  iconAlt,
  title,
  description,
  actions,
  footer,
  size = "default",
  className,
  iconClassName,
}: NoxusItemProps) => {
  const isSm = size === "sm";
  const hasFooter = !!footer;
  const iconBoxSize = isSm ? "size-6" : "size-8";

  const renderIcon = () => {
    if (!icon) return null;
    const boxClassName = cn(
      "flex items-center justify-center shrink-0 rounded border border-slate-200 bg-gray-50",
      iconBoxSize,
      iconClassName
    );
    if (typeof icon === "string") {
      return (
        <div className={boxClassName}>
          <div className={cn("relative", isSm ? "size-3" : "size-4")}>
            <ImageFallback src={icon} alt={iconAlt ?? ""} fill />
          </div>
        </div>
      );
    }
    const IconComponent = icon;
    return (
      <div className={boxClassName}>
        <IconComponent
          size={isSm ? 12 : 16}
          strokeWidth={1.75}
          className="text-foreground"
        />
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col shrink-0 w-full rounded-lg border border-border bg-background overflow-hidden",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-3 px-3 py-2.5",
          hasFooter && "border-b border-border",
          description && "py-2"
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-visible">
          {renderIcon()}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-foreground leading-tight">
              {title}
            </span>
            {description && (
              <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-regular text-muted-foreground leading-tight">
                {description}
              </span>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        )}
      </div>

      {footer?.type === "actions" && (
        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
          {footer.label && (
            <span className="text-xs font-medium text-muted-foreground">
              {footer.label}
            </span>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {footer.children}
          </div>
        </div>
      )}

      {footer?.type === "accordion" && (
        <Accordion
          type="single"
          collapsible
          defaultValue={footer.defaultOpen ? "item" : undefined}
        >
          <AccordionItem value="item" className="border-b-0">
            <AccordionTrigger className="items-center px-3 py-2.5 text-xs font-medium text-muted-foreground hover:no-underline [&>svg]:size-4">
              {footer.label}
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-2.5 text-sm text-foreground">
              {footer.children}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export { NoxusItem };
export type { NoxusItemProps, NoxusItemFooter };
